import {groupBy} from '@benchling/js-utils';
import produce from 'immer';
import isEqual from 'react-fast-compare';
import {indexBy, uniq} from 'underscore';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  areColumnDataTypesCompatible,
  guessColumnDataType,
} from 'notebook/structured-tables/columns/ColumnDataType';
import {
  StructuredTableColumnMapping,
  StructuredTableMapping,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

/**
 * Map from table uuid to all the mappings that have that table as a source or destination,
 * depending on which map it's in.
 */
type StructuredTableMappingsUUIDMap = Record<string, Array<StructuredTableMapping> | undefined>;

export interface StructuredTableMappingsState {
  structuredTableMappings: Array<StructuredTableMapping>;
  // the below maps are for lookup convenience
  mappingsBySourceTableUuid: StructuredTableMappingsUUIDMap;
  mappingsByDestinationTableUuid: StructuredTableMappingsUUIDMap;
}

export const EMPTY_STRUCTURED_TABLE_MAPPING_STATE: StructuredTableMappingsState = {
  structuredTableMappings: [],
  mappingsBySourceTableUuid: {},
  mappingsByDestinationTableUuid: {},
};

export const StructuredTableMappingsGlobalState = makeUseGlobalClientState<StructuredTableMappingsState>(
  EMPTY_STRUCTURED_TABLE_MAPPING_STATE
);

/** A hook to get the state of StructuredTableMappings for a particular entry or template id */
export const useStructuredTableMappingsState = StructuredTableMappingsGlobalState.useGlobalClientState;

/** A utility to read from and edit StructuredTableMappingsState in an easier and immutable way. Always update
 * using the Manager rather than modifying state directly. */
export const StructuredTableMappingsStateManager = {
  // GETTERS and HELPERS
  // these return information from the state (read-only)

  /** Returns true when there are any mappings from a specific source table */
  hasMappingsFromSourceTable: (state: StructuredTableMappingsState, sourceTableUuid: string): boolean =>
    Boolean(StructuredTableMappingsStateManager.getMappingsForSourceTable(state, sourceTableUuid).length),

  /** Get all mappings with a specific source table */
  getMappingsForSourceTable: (
    state: StructuredTableMappingsState,
    sourceTableUuid: string
  ): Array<StructuredTableMapping> => getMappingsForTable(state, sourceTableUuid, 'source'),

  /** Get all mappings with a specific destination table */
  getMappingsForDestinationTable: (
    state: StructuredTableMappingsState,
    destinationTableUuid: string
  ): Array<StructuredTableMapping> => getMappingsForTable(state, destinationTableUuid, 'destination'),

  /** Get all mappings with a specific source column */
  getMappingsForSourceColumn: (
    state: StructuredTableMappingsState,
    sourceTableUuid: string,
    sourceColumnUuid: string
  ): Array<StructuredTableMapping> =>
    getMappingsForColumn(state, sourceTableUuid, sourceColumnUuid, 'source'),

  /** Get all mappings with a specific destination column */
  getMappingsForDestinationColumn: (
    state: StructuredTableMappingsState,
    destinationTableUuid: string,
    destinationColumnUuid: string
  ): Array<StructuredTableMapping> =>
    getMappingsForColumn(state, destinationTableUuid, destinationColumnUuid, 'destination'),

  /**
   * Get the replication factor for the destination table from the current state, which could be undefined if
   * the replication factor was not specified. If no mappings exist for the destination table, returns null.
   */
  getReplicationFactorForDestinationTable(
    state: StructuredTableMappingsState,
    destinationTableUuid: string
  ): number | undefined | null {
    const replicationFactors = StructuredTableMappingsStateManager.getMappingsForDestinationTable(
      state,
      destinationTableUuid
    ).flatMap((tableMapping) => tableMapping.replicationFactor);
    if (replicationFactors.length === 0) {
      return null;
    }
    assert(
      _.unique(replicationFactors).length === 1,
      'Destination column should have only one replication factor',
      {replicationFactors}
    );
    return replicationFactors[0];
  },

  // UPDATERS
  // these all return a new state

  /** Set the state with new mappings */
  setStructuredTableMappings: (
    structuredTableMappings: Array<StructuredTableMapping>
  ): StructuredTableMappingsState => ({
    structuredTableMappings,
    ...makeTableMappingUuidMaps(structuredTableMappings),
  }),

  /** Remove any table mappings from state where the source or destination table is the given table. */
  removeTableMappings: (
    state: StructuredTableMappingsState,
    tableUuid: string
  ): StructuredTableMappingsState => {
    const filteredTableMappings = state.structuredTableMappings.filter(
      (tableMapping) =>
        tableMapping.sourceTableProvenanceUuid !== tableUuid &&
        tableMapping.destinationTableProvenanceUuid !== tableUuid
    );
    // If we didn't remove any mappings, return the same state to avoid unnecessary re-renders
    return filteredTableMappings.length === state.structuredTableMappings.length
      ? state
      : StructuredTableMappingsStateManager.setStructuredTableMappings(filteredTableMappings);
  },

  /** Remove any table mappings from state that have a particular destination column */
  removeDestinationColumnMappings: (
    state: StructuredTableMappingsState,
    destinationTableUuid: string,
    destinationColumnUuid: string
  ): StructuredTableMappingsState => {
    const filteredTableMappings = state.structuredTableMappings
      .map(
        (tableMapping): StructuredTableMapping =>
          tableMapping.destinationTableProvenanceUuid === destinationTableUuid
            ? {
                ...tableMapping,
                structuredTableColumnMappings: tableMapping.structuredTableColumnMappings.filter(
                  (columnMapping) =>
                    columnMapping.destinationTableColumnProvenanceUuid !== destinationColumnUuid
                ),
              }
            : tableMapping
      )
      .filter((tableMapping) => tableMapping.structuredTableColumnMappings.length);
    return StructuredTableMappingsStateManager.setStructuredTableMappings(filteredTableMappings);
  },

  /** Remove any table mappings from state that reference non-existant columns or have incompatible types */
  removeInvalidColumnMappings: (
    state: StructuredTableMappingsState,
    tables: Array<UnknownStructuredTable>
  ): StructuredTableMappingsState => {
    const tablesByUuid: Record<string, UnknownStructuredTable | undefined> = indexBy(
      tables,
      (table) => table.provenanceUuid
    );
    const filteredTableMappings = state.structuredTableMappings
      .map(
        (tableMapping): StructuredTableMapping => ({
          ...tableMapping,
          structuredTableColumnMappings: tableMapping.structuredTableColumnMappings.filter(
            (columnMapping) => {
              const sourceTable = tablesByUuid[tableMapping.sourceTableProvenanceUuid];
              const destinationTable = tablesByUuid[tableMapping.destinationTableProvenanceUuid];
              // If either table is missing, the mapping should be removed
              if (!sourceTable || !destinationTable) {
                return false;
              }
              return isTableColumnMappingValid(columnMapping, sourceTable, destinationTable);
            }
          ),
        })
      )
      .filter((tableMapping) => tableMapping.structuredTableColumnMappings.length);
    // If there are no changes, return the same state to avoid unnecessary re-renders
    return isEqual(filteredTableMappings, state.structuredTableMappings)
      ? state
      : StructuredTableMappingsStateManager.setStructuredTableMappings(filteredTableMappings);
  },

  /** Add new table mappings to an existing state, ensuring that mappings with the same tables are merged */
  addStructuredTableMappings: (
    state: StructuredTableMappingsState,
    newTableMappings: Array<StructuredTableMapping>
  ): StructuredTableMappingsState => {
    const updatedTableMappings = produce(state.structuredTableMappings, (draft) => {
      for (const newTableMapping of newTableMappings) {
        const existingTableMapping = draft.find(
          (existingMapping) =>
            existingMapping.sourceTableProvenanceUuid === newTableMapping.sourceTableProvenanceUuid &&
            existingMapping.destinationTableProvenanceUuid === newTableMapping.destinationTableProvenanceUuid
        );
        if (existingTableMapping) {
          existingTableMapping.structuredTableColumnMappings = mergeTableColumnMappings(
            existingTableMapping.structuredTableColumnMappings,
            newTableMapping.structuredTableColumnMappings
          );
        } else {
          draft.push(newTableMapping);
        }
      }
    });
    return StructuredTableMappingsStateManager.setStructuredTableMappings(updatedTableMappings);
  },

  /** Replace all the mappings for a given destination column */
  replaceDestinationColumnMappings: (
    state: StructuredTableMappingsState,
    destinationTableUuid: string,
    destinationColumnUuid: string,
    newTableMappings: Array<StructuredTableMapping>
  ): StructuredTableMappingsState => {
    const removedState = StructuredTableMappingsStateManager.removeDestinationColumnMappings(
      state,
      destinationTableUuid,
      destinationColumnUuid
    );
    return StructuredTableMappingsStateManager.addStructuredTableMappings(removedState, newTableMappings);
  },
};

// HELPERS

/** Group mappings by their table uuids */
function makeTableMappingUuidMaps(structuredTableMappings: Array<StructuredTableMapping>): {
  mappingsBySourceTableUuid: StructuredTableMappingsUUIDMap;
  mappingsByDestinationTableUuid: StructuredTableMappingsUUIDMap;
} {
  return {
    mappingsBySourceTableUuid: groupBy(
      structuredTableMappings,
      (mapping) => mapping.sourceTableProvenanceUuid
    ),
    mappingsByDestinationTableUuid: groupBy(
      structuredTableMappings,
      (mapping) => mapping.destinationTableProvenanceUuid
    ),
  };
}

/** Gather the mappings for a particular source or destination table */
function getMappingsForTable(
  state: StructuredTableMappingsState,
  tableUuid: string,
  sourceOrDestination: 'source' | 'destination'
): Array<StructuredTableMapping> {
  const uuidMap =
    sourceOrDestination === 'source' ? state.mappingsBySourceTableUuid : state.mappingsByDestinationTableUuid;

  return uuidMap[tableUuid] ?? [];
}

/** Gather the mappings for a particular source or destination column */
function getMappingsForColumn(
  state: StructuredTableMappingsState,
  tableUuid: string,
  columnUuid: string,
  sourceOrDestination: 'source' | 'destination'
): Array<StructuredTableMapping> {
  const columnUuidPath =
    sourceOrDestination === 'source'
      ? 'sourceTableColumnProvenanceUuid'
      : 'destinationTableColumnProvenanceUuid';

  return (
    getMappingsForTable(state, tableUuid, sourceOrDestination)
      .map(
        (tableMapping): StructuredTableMapping => ({
          ...tableMapping,
          // Filter structuredTableColumnMappings to only columns that match columnUuid
          structuredTableColumnMappings: tableMapping.structuredTableColumnMappings.filter(
            (columnMapping) => columnMapping[columnUuidPath] === columnUuid
          ),
        })
      )
      // Only return table mappings that are nonempty after we filtered the column list
      .filter((tableMapping) => tableMapping.structuredTableColumnMappings.length)
  );
}

/** Merges two table mappings for the same tables, ensuring no duplicates in their column mappings */
function mergeTableColumnMappings(
  existingColumnMappings: Array<StructuredTableColumnMapping>,
  newColumnMappings: Array<StructuredTableColumnMapping>
): Array<StructuredTableColumnMapping> {
  return uniq(
    [...existingColumnMappings, ...newColumnMappings],
    false,
    (columnMapping) =>
      columnMapping.sourceTableColumnProvenanceUuid + columnMapping.destinationTableColumnProvenanceUuid
  );
}

/**
 * Keep mapping if both source and destination column of a table exist and their types are still compatible.
 */
function isTableColumnMappingValid(
  columnMapping: StructuredTableColumnMapping,
  sourceTable: UnknownStructuredTable,
  destinationTable: UnknownStructuredTable
): boolean {
  const sourceColumnIndex = sourceTable.columns.findIndex(
    (column) => column.provenanceUuid === columnMapping.sourceTableColumnProvenanceUuid
  );
  const destinationColumnIndex = destinationTable.columns.findIndex(
    (column) => column.provenanceUuid === columnMapping.destinationTableColumnProvenanceUuid
  );
  if (sourceColumnIndex === -1 || destinationColumnIndex === -1) {
    // If either column doesn't exist, it's invalid
    return false;
  }
  if (BenchlingConfig.ELN_ENABLE_LOOKUP_TABLE_MAPPING) {
    // Check if the columns are still type-compatible
    const sourceTableColumnDefinitions =
      getStructuredTableAdapterFromTable(sourceTable).getColumnDefinitions(sourceTable);
    const destinationTableColumnDefinitions =
      getStructuredTableAdapterFromTable(destinationTable).getColumnDefinitions(destinationTable);

    const sourceColumnDefinition = sourceTableColumnDefinitions[sourceColumnIndex];
    const destinationColumnDefinition = destinationTableColumnDefinitions[destinationColumnIndex];
    if (!sourceColumnDefinition || !destinationColumnDefinition) {
      return false;
    }
    const sourceColumnDataType = guessColumnDataType(sourceColumnDefinition, sourceTable);
    const destinationColumnDataType = guessColumnDataType(destinationColumnDefinition, destinationTable);
    // if either column has a null data type, we don't expect the columns to be mapped.
    // eslint-disable-next-line benchlint/no-eqeqeq-null -- null is distinct from undefined here
    if (sourceColumnDataType === null || destinationColumnDataType === null) {
      return false;
    }
    // if the data types are undefined because they're not loaded yet, we can't determine if they're
    // invalid so keep them
    if (sourceColumnDataType === undefined || destinationColumnDataType === undefined) {
      return true;
    }
    return areColumnDataTypesCompatible(sourceColumnDataType, destinationColumnDataType);
  }
  return true;
}
